// src/pages/ArretTravail.jsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import { CONFIG } from '../config/config';
import { useUser } from '../context/UserContext';
import { FileText, ArrowLeft, RotateCcw, CheckCircle } from 'lucide-react';

export default function ArretTravail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = sessionStorage.getItem('token') || localStorage.getItem('token');
  const { profile, etablissement } = useUser();

  const [patient, setPatient] = useState(null);
  const [paramsAT, setParamsAT] = useState(null);
  const [treeConfig, setTreeConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [currentStepId, setCurrentStepId] = useState('start');
  const [historyStack, setHistoryStack] = useState([]);
  const [infoMessage, setInfoMessage] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        
        // Requêtes lancées en parallèle pour gagner du temps
        const [resPatient, resParams, resTree] = await Promise.all([
          fetch(`${CONFIG.API_URL}/passages/${id}`, { headers }),
          fetch(`${CONFIG.API_URL}/arret-travail/params-at`, { headers }),
          fetch(`${CONFIG.API_URL}/arret-travail/decision-tree`, { headers })
        ]);

        if (!resPatient.ok) throw new Error('Erreur lors du chargement du patient');

        setPatient(await resPatient.json());
        setParamsAT(await resParams.json());
        setTreeConfig(await resTree.json());
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, token]);

  // Envoi To Do automatique corrigé
  const createAutoToDo = async ({ comment, urgence = false, roles = ['secretaire'] }) => {
    const etablissementId =
      etablissement?.etablissement_id ||
      profile?.profile_default_etablissement_id ||
      profile?.initEtab?.etablissement_id;

    if (!etablissementId) {
      alert("⚠️ Impossible de trouver l'établissement cible.");
      return;
    }

    const cibles = roles.map((role) => ({ role, etablissementId }));
    
    // 1. Sécurisation de la date de naissance (le backend l'exige absolument)
    const rawDate = patient?.dateNaissance || patient?.ddn || patient?.date_naissance;
    let formattedDdn = '1900-01-01'; // Fallback par défaut si aucune date
    try { 
      if (rawDate) {
        const parsed = new Date(rawDate);
        if (!isNaN(parsed.getTime())) {
          formattedDdn = parsed.toISOString().split('T')[0];
        }
      }
    } catch (e) {}

    // 2. Sécurisation du Sexe (Mappé sur "H" ou "F" obligatoirement)
    let finalSexe = 'F';
    if (patient?.sexe === 'M' || patient?.sexe === 'H' || patient?.sexe === 'm') {
      finalSexe = 'H';
    }

    const payload = {
      cibles,
      profilDemandeurId: profile?.profile_id || null,
      passageId: id, // Ajouté par précaution
      paramDefTodo: {
        key: 'Arret_Travail',
        name: 'Arrêt de travail',
        orderInMenu: 40,
        entryToDoPII: ['nom', 'prenom', 'date_naissance', 'sexe'],
        entry: ['urgence', 'comment'],
        dropdowns: { sexe: ['H', 'F'] },
        lookupPassage_PII: [],
        lookupDB_Passage: [],
        lookupToDo_PII: ['nom', 'prenom', 'date_naissance'],
        autoField: ['dateCreationToDo', 'PrenomNomDDN'],
        displayOrder: ['PrenomNomDDN', 'sexe', 'comment'],
        copyIcon: ['PrenomNomDDN'],
      },
      todo: {
        entryTodoPii: {
          // 3. Pas de chaînes vides, sinon le trim() du backend lève une 422
          nom: patient?.nom || 'Inconnu',
          prenom: patient?.prenom || 'Inconnu',
          date_naissance: formattedDdn,
          sexe: finalSexe,
        },
        entry: { urgence: Boolean(urgence), comment: comment || '' },
      },
    };

    try {
      const res = await fetch(`${CONFIG.API_URL}/todos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        // 4. On récupère enfin le vrai message d'erreur du backend !
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.message || 'Erreur API inconnue');
      }
      
      alert(`✅ To Do transmise (${roles.join(' & ')}) !`);
    } catch (err) {
      console.error('Erreur To Do:', err);
      // L'alerte affichera exactement ce qui a bloqué la requête 422 (ex: "Le champ prenom est requis")
      alert(`⚠️ Erreur lors de l’envoi de la To Do : ${err.message}`);
    }
  };

  const handleChoiceClick = (choice) => {
    if (choice.next_step) {
      setHistoryStack((prev) => [...prev, currentStepId]);
      setCurrentStepId(choice.next_step);
      return;
    }
    if (choice.action === 'navigate') {
      navigate(choice.url);
    } else if (choice.action === 'link') {
        if (choice.url?.startsWith('intent://')) {
            window.location.href = choice.url;
        } else {
            window.open(choice.url, '_blank');
        }
    } else if (choice.action === 'todo') {
      createAutoToDo(choice.todoConfig);
    }
  };

  const goBack = () => {
    if (historyStack.length === 0) return;
    const previousStack = [...historyStack];
    const previousStep = previousStack.pop();
    setHistoryStack(previousStack);
    setCurrentStepId(previousStep);
  };

  const resetAll = () => {
    setHistoryStack([]);
    setCurrentStepId('start');
  };

  if (loading || !paramsAT || !treeConfig) {
    return (
      <PageLayout title="Arrêt de travail">
        <div className="p-6 text-slate-600">Chargement…</div>
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout title="Arrêt de travail">
        <div className="p-6 text-red-600">Erreur : {error}</div>
      </PageLayout>
    );
  }

  // Ces calculs ne se font que lorsque paramsAT est complètement chargé
  const DOCTOLIB_CPS_TARGETS = paramsAT.doctolibCpsTargets;
  const trigramme = (etablissement?.etablissement_trigramme || profile?.initEtab?.etablissement_trigramme || 'AUT').toUpperCase();
  const rawTarget = DOCTOLIB_CPS_TARGETS[trigramme] || DOCTOLIB_CPS_TARGETS.AUT;
  const cleanTarget = rawTarget.replace(/^http?:\/\//, '');
  const doctolibIntentUrl = `intent://${cleanTarget}#Intent;scheme=http;package=com.sec.android.app.sbrowser;action=android.intent.action.VIEW;end;`;

  const getDecisionTree = (id, doctolibIntentUrl) => {
    let stringifiedTree = JSON.stringify(treeConfig);
    stringifiedTree = stringifiedTree.replaceAll('{id}', id);
    stringifiedTree = stringifiedTree.replaceAll('{doctolibIntentUrl}', doctolibIntentUrl);
    return JSON.parse(stringifiedTree);
  };

  const currentStep = getDecisionTree(id, doctolibIntentUrl)[currentStepId];

  return (
    <PageLayout title="Arrêt de travail">
      <div className="w-full mx-auto p-4 sm:p-6">
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={() => navigate(`/fiche/${id}`)}
            className="p-2 rounded-lg hover:bg-slate-200 text-slate-700 transition bg-slate-100"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2 text-slate-800">
              <FileText className="h-6 w-6 text-[#E1251B]" />
              Arrêt de Travail
            </h1>
            <p className="text-sm text-slate-500 font-medium">
              Patient : {patient?.civilite} {String(patient?.nom || '').toUpperCase()} {patient?.prenom || ''}
            </p>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
          <div className="flex justify-between items-center border-b border-slate-100 pb-4 mb-6">
            <button
              onClick={goBack}
              disabled={historyStack.length === 0}
              className="px-4 py-2 rounded-lg text-sm font-semibold border transition-all disabled:opacity-40 disabled:cursor-not-allowed border-slate-300 text-slate-700 hover:bg-slate-50"
            >
              ← Retour
            </button>
            <button
              onClick={resetAll}
              className="px-4 py-2 rounded-lg text-sm font-semibold border border-[#E1251B] text-[#E1251B] hover:bg-[#E1251B] hover:text-white transition-all flex items-center gap-2"
            >
              <RotateCcw className="w-4 h-4" />
              Recommencer
            </button>
          </div>

          <h2 className="text-xl font-bold text-slate-800 mb-6 leading-snug">
            {currentStep.question}
          </h2>

          {currentStepId === 'fin_parcours' ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <CheckCircle className="w-16 h-16 text-emerald-500 mb-4" />
              <p className="text-lg text-slate-600 font-medium">Le parcours est terminé.</p>
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              {currentStep.choix.map((choice, idx) => {
                
                // On affiche un texte si l'action est 'info'
                if (choice.action === 'info') {
                  return (
                    <div key={idx} className="p-4 bg-red-50 text-red-800 rounded-lg text-center font-medium border border-red-100">
                      {choice.label}
                    </div>
                  );
                }

                // SINON, on affiche le bouton classique
                return (
                  <button
                    key={idx}
                    onClick={() => handleChoiceClick(choice)}
                    className="w-full text-left px-5 py-4 rounded-xl bg-[#E1251B] hover:bg-[#c11e15] text-white font-semibold shadow-sm transition-all transform hover:-translate-y-[1px] active:translate-y-0"
                  >
                    {choice.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </PageLayout>
  );
}